import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ==================== 3D PARTICLE CANVAS ====================
const ParticleCanvas = () => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const particles = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Init particles
    particles.current = Array.from({ length: 60 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 2 + 0.5,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      alpha: Math.random() * 0.6 + 0.2,
      color: Math.random() > 0.5 ? '212,175,55' : '220,38,38',
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.current.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color},${p.alpha})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
      });
      animRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: 0.5 }}
    />
  );
};

// ==================== 3D CARD COMPONENT ====================
const Card3D = ({ card, onClick }) => {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const rotateX = ((y - cy) / cy) * -12;
    const rotateY = ((x - cx) / cx) * 12;
    el.style.transform = `perspective(600px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.05,1.05,1.05)`;
    const glare = el.querySelector('.card-glare');
    if (glare) {
      glare.style.background = `radial-gradient(circle at ${(x / rect.width) * 100}% ${(y / rect.height) * 100}%, rgba(255,215,0,0.25) 0%, transparent 70%)`;
    }
  };

  const handleMouseLeave = () => {
    if (cardRef.current) {
      cardRef.current.style.transform = 'perspective(600px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)';
    }
  };

  const getRarityBorder = (rarity) => {
    const borders = {
      'Secret Rare': 'border-yellow-400 shadow-yellow-400/40',
      'Ultra Rare': 'border-purple-400 shadow-purple-400/40',
      'Super Rare': 'border-blue-400 shadow-blue-400/40',
      'Rare': 'border-green-400 shadow-green-400/40',
      'Common': 'border-gray-600 shadow-gray-600/20',
    };
    return borders[rarity] || borders['Common'];
  };

  const getRarityGlow = (rarity) => {
    const glows = {
      'Secret Rare': 'rgba(212,175,55,0.6)',
      'Ultra Rare': 'rgba(168,85,247,0.5)',
      'Super Rare': 'rgba(59,130,246,0.5)',
      'Rare': 'rgba(34,197,94,0.5)',
      'Common': 'rgba(75,85,99,0.3)',
    };
    return glows[rarity] || glows['Common'];
  };

  return (
    <div
      ref={cardRef}
      className={`relative cursor-pointer border-2 rounded-xl overflow-hidden transition-all duration-200 ${getRarityBorder(card.rarity)} shadow-lg`}
      style={{ transformStyle: 'preserve-3d', willChange: 'transform' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={() => onClick(card)}
      data-testid={`carta-card-${card.id}`}
    >
      <div className="card-glare absolute inset-0 z-10 pointer-events-none rounded-xl transition-all duration-100" />
      <div className="aspect-[63/88] bg-gray-900 overflow-hidden">
        <img
          src={card.image}
          alt={card.name}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = `https://placehold.co/200x280/1a1a2e/d4af37?text=${encodeURIComponent(card.name?.substring(0, 8) || 'Card')}`;
          }}
        />
      </div>
      <div className="p-2 bg-gray-950">
        <p className="text-white text-xs font-semibold truncate">{card.name}</p>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs font-bold text-yellow-400">${card.price}</span>
          {card.stock !== undefined && (
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${card.stock > 3 ? 'bg-green-900/60 text-green-400' : card.stock > 0 ? 'bg-yellow-900/60 text-yellow-400' : 'bg-red-900/60 text-red-400'}`}>
              {card.stock > 0 ? `x${card.stock}` : 'Agotada'}
            </span>
          )}
        </div>
      </div>
      {/* Rarity glow overlay */}
      <div
        className="absolute inset-0 pointer-events-none rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ boxShadow: `inset 0 0 20px ${getRarityGlow(card.rarity)}` }}
      />
    </div>
  );
};

// ==================== EXPANSION CARD (3D) ====================
const ExpansionCard3D = ({ expansion, onClick }) => {
  const ref = useRef(null);

  const handleMouseMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const rotateX = ((y - cy) / cy) * -8;
    const rotateY = ((x - cx) / cx) * 8;
    el.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(10px)`;
  };

  const handleMouseLeave = () => {
    if (ref.current) ref.current.style.transform = 'perspective(800px) rotateX(0deg) rotateY(0deg) translateZ(0px)';
  };

  return (
    <div
      ref={ref}
      className="cursor-pointer rounded-2xl overflow-hidden border border-red-900/50 hover:border-yellow-500/70 transition-all duration-300 group"
      style={{ transformStyle: 'preserve-3d', background: 'linear-gradient(135deg, #0f0f1a 0%, #1a0a0a 100%)' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={() => onClick(expansion)}
      data-testid={`expansion-${expansion.id}`}
    >
      <div className="relative overflow-hidden" style={{ height: '160px' }}>
        <div
          className="w-full h-full flex items-center justify-center"
          style={{ background: expansion.gradient || 'linear-gradient(135deg, #1a0a0a, #2d1515)' }}
        >
          <span className="text-5xl group-hover:scale-110 transition-transform duration-300">{expansion.icon}</span>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="text-white font-black text-sm truncate" style={{ fontFamily: 'Cinzel, serif' }}>{expansion.name}</h3>
          <p className="text-yellow-400/80 text-xs mt-0.5">{expansion.cardCount} cartas disponibles</p>
        </div>
      </div>
      <div className="px-3 py-2 flex items-center justify-between">
        <span className="text-xs text-gray-400">{expansion.year}</span>
        <span className="text-xs text-red-400 font-semibold group-hover:text-yellow-400 transition-colors">Ver colección →</span>
      </div>
    </div>
  );
};

// ==================== PRODUCT DETAIL MODAL ====================
const CardDetailModal = ({ card, onClose, onAddToCart }) => {
  if (!card) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}
      style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(12px)' }}>
      <div
        className="relative max-w-2xl w-full rounded-2xl overflow-hidden border border-yellow-500/30"
        style={{ background: 'linear-gradient(135deg, #0d0d1a 0%, #1a0505 100%)', boxShadow: '0 0 60px rgba(212,175,55,0.15)' }}
        onClick={e => e.stopPropagation()}
        data-testid="cartas-detail-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white flex items-center justify-center text-sm transition-colors"
        >✕</button>

        <div className="flex flex-col sm:flex-row gap-0">
          {/* Left: Image */}
          <div className="sm:w-56 flex-shrink-0 p-6">
            <div className="aspect-[63/88] rounded-xl overflow-hidden border-2 border-yellow-500/30 shadow-2xl"
              style={{ boxShadow: '0 0 30px rgba(212,175,55,0.2)' }}>
              <img
                src={card.imageFull || card.image}
                alt={card.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://placehold.co/300x420/0d0d1a/d4af37?text=${encodeURIComponent(card.name?.substring(0, 10) || 'Card')}`;
                }}
              />
            </div>
          </div>

          {/* Right: Info */}
          <div className="flex-1 p-6 flex flex-col">
            <h2 className="text-xl font-black text-white mb-3" style={{ fontFamily: 'Cinzel, serif' }}>{card.name}</h2>

            <div className="flex flex-wrap gap-2 mb-4">
              {card.rarity && (
                <span className="px-3 py-1 rounded-full text-xs font-bold" style={{
                  background: card.rarity === 'Secret Rare' ? 'linear-gradient(90deg,#d4af37,#f5e06e,#d4af37)' :
                    card.rarity === 'Ultra Rare' ? 'linear-gradient(90deg,#7c3aed,#a855f7)' :
                    card.rarity === 'Super Rare' ? 'linear-gradient(90deg,#1d4ed8,#3b82f6)' :
                    card.rarity === 'Rare' ? 'linear-gradient(90deg,#15803d,#22c55e)' : '#374151',
                  color: card.rarity === 'Secret Rare' ? '#000' : '#fff'
                }}>
                  {card.rarity}
                </span>
              )}
              {card.type && (
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-900/60 text-red-300 capitalize">{card.type}</span>
              )}
            </div>

            {(card.atk !== undefined || card.def !== undefined || card.level) && (
              <div className="flex flex-wrap gap-4 mb-4 p-3 rounded-xl" style={{ background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.15)' }}>
                {card.level && <div className="text-center"><div className="text-yellow-400 font-black">{card.level}</div><div className="text-gray-500 text-xs">NIVEL</div></div>}
                {card.atk !== undefined && <div className="text-center"><div className="text-red-400 font-black">{card.atk}</div><div className="text-gray-500 text-xs">ATK</div></div>}
                {card.def !== undefined && <div className="text-center"><div className="text-blue-400 font-black">{card.def}</div><div className="text-gray-500 text-xs">DEF</div></div>}
                {card.attribute && <div className="text-center"><div className="text-orange-400 font-black text-xs">{card.attribute}</div><div className="text-gray-500 text-xs">ATRIB.</div></div>}
              </div>
            )}

            {card.description && (
              <div className="mb-4 p-3 rounded-xl text-xs text-gray-400 leading-relaxed max-h-20 overflow-y-auto"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                {card.description}
              </div>
            )}

            <div className="mt-auto">
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-gray-400 text-sm">Precio:</span>
                <span className="text-3xl font-black" style={{ background: 'linear-gradient(90deg,#dc2626,#f97316)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  ${card.price}
                </span>
              </div>

              <button
                onClick={() => { onAddToCart(card); onClose(); }}
                disabled={card.stock === 0}
                className="w-full py-3 rounded-xl font-black text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg hover:scale-[1.02]"
                style={{ background: 'linear-gradient(135deg,#dc2626,#b91c1c)', boxShadow: '0 4px 20px rgba(220,38,38,0.3)' }}
                data-testid="cartas-add-cart"
              >
                Agregar al Carrito
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
const CartasStorePage = ({ onAddToCart }) => {
  const [view, setView] = useState('home'); // 'home' | 'set' | 'product'
  const [selectedExpansion, setSelectedExpansion] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [cards, setCards] = useState([]);
  const [expansions, setExpansions] = useState([]);
  const [loadingExpansions, setLoadingExpansions] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRarity, setFilterRarity] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  // Era gradients for visual variety
  const ERA_GRADIENTS = [
    'linear-gradient(135deg,#1a0505,#3d0000)',
    'linear-gradient(135deg,#1a1000,#3d2900)',
    'linear-gradient(135deg,#001a05,#003d12)',
    'linear-gradient(135deg,#1a001a,#3d003d)',
    'linear-gradient(135deg,#001a1a,#003d3d)',
    'linear-gradient(135deg,#0d0d1a,#00001a)',
    'linear-gradient(135deg,#0d0a00,#3d2d00)',
    'linear-gradient(135deg,#1a0510,#2d0020)',
    'linear-gradient(135deg,#000d1a,#001540)',
  ];
  const ERA_ICONS = ['🐉','⚡','✨','🕸️','💫','🔗','🌊','🔥','🌙','👁️','⚔️','🌸','🎴','💎','🌟'];

  useEffect(() => {
    loadExpansions();
  }, []);

  const loadExpansions = async () => {
    setLoadingExpansions(true);
    try {
      const res = await axios.get(`${API}/expansiones?limit=500`);
      const data = res.data.expansiones || [];
      if (data.length > 0) {
        setExpansions(data.map((exp, i) => ({
          id: exp.id || exp._id || `exp-${i}`,
          name: exp.nombre || exp.name || exp.set_name || `Expansión ${i+1}`,
          year: exp.año || exp.year || exp.tcg_date?.substring(0,4) || '',
          cardCount: exp.num_cards || exp.card_count || exp.total_cards || '?',
          icon: ERA_ICONS[i % ERA_ICONS.length],
          gradient: ERA_GRADIENTS[i % ERA_GRADIENTS.length],
          setCode: exp.set_code || exp.codigo || null,
          raw: exp,
        })));
      } else {
        // Fallback static if DB is empty
        setExpansions(STATIC_EXPANSIONS);
      }
    } catch (e) {
      setExpansions(STATIC_EXPANSIONS);
    }
    setLoadingExpansions(false);
  };

  const STATIC_EXPANSIONS = [
    { id: 'clasicos', name: 'Clásicos Legendarios', icon: '🐉', year: '1999-2003', cardCount: 0, gradient: 'linear-gradient(135deg,#1a0505,#3d0000)', typeFilter: 'monster' },
    { id: 'dioses', name: 'Dioses Egipcios', icon: '⚡', year: '2003-2005', cardCount: 0, gradient: 'linear-gradient(135deg,#1a1000,#3d2900)', typeFilter: 'monster' },
    { id: 'magias', name: 'Cartas Mágicas', icon: '✨', year: 'Todas las eras', cardCount: 0, gradient: 'linear-gradient(135deg,#001a05,#003d12)', typeFilter: 'spell' },
    { id: 'trampas', name: 'Cartas de Trampa', icon: '🕸️', year: 'Todas las eras', cardCount: 0, gradient: 'linear-gradient(135deg,#1a001a,#3d003d)', typeFilter: 'trap' },
    { id: 'synchro', name: 'Era Synchro & XYZ', icon: '💫', year: '2008-2013', cardCount: 0, gradient: 'linear-gradient(135deg,#001a1a,#003d3d)', typeFilter: 'monster' },
    { id: 'link', name: 'Era Link & Moderno', icon: '🔗', year: '2017-2025', cardCount: 0, gradient: 'linear-gradient(135deg,#0d0d1a,#00001a)', typeFilter: 'monster' },
  ];

  const loadCards = useCallback(async (typeFilter = null) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cards?limit=200`);
      const rawCards = res.data.cards || [];
      const mapped = rawCards.map((card, idx) => {
        let cardType = 'monster';
        const ft = card.frameType?.toLowerCase() || '';
        const t = card.type?.toLowerCase() || '';
        if (ft.includes('spell') || t.includes('spell')) cardType = 'spell';
        else if (ft.includes('trap') || t.includes('trap')) cardType = 'trap';

        let rarity = 'Common';
        if (card.card_sets?.length > 0) {
          const sr = card.card_sets[0].set_rarity || '';
          if (sr.includes('Secret')) rarity = 'Secret Rare';
          else if (sr.includes('Ultra')) rarity = 'Ultra Rare';
          else if (sr.includes('Super')) rarity = 'Super Rare';
          else if (sr.includes('Rare')) rarity = 'Rare';
        }

        let price = (Math.random() * 50 + 2).toFixed(2);
        if (card.card_prices?.length > 0) {
          const tcp = parseFloat(card.card_prices[0].tcgplayer_price || 0);
          const cmp = parseFloat(card.card_prices[0].cardmarket_price || 0);
          if (tcp > 0) price = tcp.toFixed(2);
          else if (cmp > 0) price = cmp.toFixed(2);
        }

        return {
          id: `c-${card.id || idx}`,
          dbId: card.id,
          name: card.name || 'Unknown Card',
          image: card.card_images?.[0]?.image_url_small || `https://images.ygoprodeck.com/images/cards_small/${card.id}.jpg`,
          imageFull: card.card_images?.[0]?.image_url || `https://images.ygoprodeck.com/images/cards/${card.id}.jpg`,
          price,
          rarity,
          stock: Math.floor(Math.random() * 8) + 1,
          type: cardType,
          description: card.desc || '',
          atk: card.atk,
          def: card.def,
          level: card.level,
          attribute: card.attribute,
          race: card.race,
        };
      });
      setCards(typeFilter ? mapped.filter(c => c.type === typeFilter) : mapped);
    } catch (e) {
      console.error(e);
      setCards([]);
    }
    setLoading(false);
  }, []);

  const openExpansion = (expansion) => {
    setSelectedExpansion(expansion);
    setFilterRarity('all');
    setFilterType(expansion.typeFilter || 'all');
    setSearchQuery('');
    loadCards(expansion.typeFilter !== 'all' ? expansion.typeFilter : null);
    setView('set');
  };

  const filteredCards = cards.filter(c => {
    if (filterRarity !== 'all' && c.rarity !== filterRarity) return false;
    if (filterType !== 'all' && c.type !== filterType) return false;
    if (searchQuery && !c.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  }).sort((a, b) => {
    if (sortBy === 'price_asc') return parseFloat(a.price) - parseFloat(b.price);
    if (sortBy === 'price_desc') return parseFloat(b.price) - parseFloat(a.price);
    if (sortBy === 'rarity') {
      const order = { 'Secret Rare': 0, 'Ultra Rare': 1, 'Super Rare': 2, 'Rare': 3, 'Common': 4 };
      return (order[a.rarity] ?? 5) - (order[b.rarity] ?? 5);
    }
    return a.name.localeCompare(b.name);
  });

  const rarities = ['all', 'Secret Rare', 'Ultra Rare', 'Super Rare', 'Rare', 'Common'];

  return (
    <div className="min-h-screen" style={{ background: '#080810' }}>
      {/* ====== HOME VIEW ====== */}
      {view === 'home' && (
        <>
          {/* Hero Section */}
          <div className="relative overflow-hidden" style={{ minHeight: '320px', background: 'linear-gradient(135deg,#0d0005 0%,#1a0000 50%,#0d0510 100%)' }}>
            <ParticleCanvas />
            <div className="relative z-10 flex flex-col items-center justify-center py-16 px-4 text-center">
              <div
                className="inline-block px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-4"
                style={{ background: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.4)', color: '#d4af37' }}
              >
                Colección Exclusiva
              </div>
              <h1 className="text-4xl sm:text-5xl font-black mb-4" style={{ fontFamily: 'Cinzel, serif', background: 'linear-gradient(135deg,#ffffff,#d4af37,#ffffff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Tienda de Cartas
              </h1>
              <p className="text-gray-400 max-w-lg text-sm sm:text-base">
                Explora nuestra colección completa de cartas Yu-Gi-Oh!. Desde clásicos legendarios hasta las últimas ediciones.
              </p>
            </div>
            {/* Decorative lines */}
            <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg,transparent,#d4af37,transparent)' }} />
          </div>

          {/* Expansions Grid */}
          <div className="container mx-auto px-4 py-10">
            <div className="flex items-center justify-between gap-3 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-1 h-8 rounded-full" style={{ background: 'linear-gradient(180deg,#dc2626,#d4af37)' }} />
                <h2 className="text-2xl font-black text-white" style={{ fontFamily: 'Cinzel, serif' }}>
                  {loadingExpansions ? 'Cargando...' : `${expansions.length} Expansiones`}
                </h2>
              </div>
            </div>
            {loadingExpansions ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-10 h-10 rounded-full border-4 border-red-600 border-t-transparent animate-spin" />
                <span className="text-gray-400 ml-3">Cargando expansiones...</span>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4">
                {expansions.map(exp => (
                  <ExpansionCard3D key={exp.id} expansion={exp} onClick={openExpansion} />
                ))}
              </div>
            )}

            {/* Quick Access - Ver Todas */}
            <div
              className="mt-8 p-6 rounded-2xl cursor-pointer transition-all hover:scale-[1.01] border border-red-900/50 hover:border-yellow-500/50"
              style={{ background: 'linear-gradient(135deg,#0d0005,#1a0005)' }}
              onClick={() => { setSelectedExpansion({ name: 'Todas las Cartas', id: 'all', typeFilter: null }); setFilterType('all'); loadCards(null); setView('set'); }}
              data-testid="view-all-cards"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-black text-lg" style={{ fontFamily: 'Cinzel, serif' }}>Ver Todas las Cartas</h3>
                  <p className="text-gray-400 text-sm mt-1">Explora la colección completa sin filtros</p>
                </div>
                <div className="text-4xl">🃏</div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ====== SET / CATALOG VIEW ====== */}
      {view === 'set' && (
        <>
          {/* Set Hero */}
          <div className="relative overflow-hidden" style={{ height: '160px', background: selectedExpansion?.gradient || 'linear-gradient(135deg,#0d0005,#1a0000)' }}>
            <ParticleCanvas />
            <div className="relative z-10 h-full flex flex-col justify-end p-6">
              <button
                onClick={() => setView('home')}
                className="text-gray-400 hover:text-yellow-400 text-xs mb-2 flex items-center gap-1 transition-colors w-fit"
              >
                ← Volver a Categorías
              </button>
              <h2 className="text-2xl font-black text-white" style={{ fontFamily: 'Cinzel, serif' }}>
                {selectedExpansion?.name || 'Cartas'}
              </h2>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg,transparent,#d4af37,transparent)' }} />
          </div>

          <div className="container mx-auto px-4 py-6 flex flex-col lg:flex-row gap-6">
            {/* Sidebar Filters */}
            <aside className="lg:w-56 flex-shrink-0">
              <div className="rounded-2xl p-4 sticky top-24 border border-gray-800" style={{ background: 'rgba(10,10,20,0.8)' }}>
                <h3 className="text-yellow-400 font-black text-sm mb-4 uppercase tracking-wider">Filtros</h3>

                {/* Search */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Buscar carta..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                    data-testid="cartas-search"
                  />
                </div>

                {/* Rarity Filter */}
                <div className="mb-4">
                  <p className="text-gray-400 text-xs font-semibold uppercase mb-2">Rareza</p>
                  <div className="space-y-1">
                    {rarities.map(r => (
                      <button
                        key={r}
                        onClick={() => setFilterRarity(r)}
                        className={`w-full text-left px-3 py-1.5 rounded-lg text-xs transition-all ${filterRarity === r ? 'text-yellow-400 font-bold' : 'text-gray-400 hover:text-white'}`}
                        style={{ background: filterRarity === r ? 'rgba(212,175,55,0.15)' : 'transparent' }}
                        data-testid={`rarity-filter-${r}`}
                      >
                        {r === 'all' ? 'Todas' : r}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Type Filter */}
                <div className="mb-4">
                  <p className="text-gray-400 text-xs font-semibold uppercase mb-2">Tipo</p>
                  <div className="space-y-1">
                    {[{ id: 'all', label: 'Todos' }, { id: 'monster', label: 'Monstruos' }, { id: 'spell', label: 'Magias' }, { id: 'trap', label: 'Trampas' }].map(t => (
                      <button
                        key={t.id}
                        onClick={() => setFilterType(t.id)}
                        className={`w-full text-left px-3 py-1.5 rounded-lg text-xs transition-all ${filterType === t.id ? 'text-yellow-400 font-bold' : 'text-gray-400 hover:text-white'}`}
                        style={{ background: filterType === t.id ? 'rgba(212,175,55,0.15)' : 'transparent' }}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sort */}
                <div>
                  <p className="text-gray-400 text-xs font-semibold uppercase mb-2">Ordenar</p>
                  <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg text-white text-xs focus:outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    <option value="name">Nombre A-Z</option>
                    <option value="price_asc">Precio ↑</option>
                    <option value="price_desc">Precio ↓</option>
                    <option value="rarity">Rareza</option>
                  </select>
                </div>
              </div>
            </aside>

            {/* Cards Grid */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-400 text-sm">
                  <span className="text-yellow-400 font-bold">{filteredCards.length}</span> cartas encontradas
                </p>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="w-10 h-10 rounded-full border-4 border-red-600 border-t-transparent animate-spin" />
                  <span className="text-gray-400 ml-3">Cargando cartas...</span>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
                  {filteredCards.map(card => (
                    <Card3D key={card.id} card={card} onClick={setSelectedCard} />
                  ))}
                </div>
              )}

              {!loading && filteredCards.length === 0 && (
                <div className="text-center py-16">
                  <div className="text-5xl mb-4">🔍</div>
                  <p className="text-gray-400">No se encontraron cartas con estos filtros</p>
                  <button onClick={() => { setFilterRarity('all'); setFilterType('all'); setSearchQuery(''); }}
                    className="mt-4 px-6 py-2 rounded-xl text-sm font-semibold text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/10 transition-colors">
                    Limpiar filtros
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Card Detail Modal */}
      {selectedCard && (
        <CardDetailModal
          card={selectedCard}
          onClose={() => setSelectedCard(null)}
          onAddToCart={(card) => {
            if (onAddToCart) onAddToCart({ ...card, product_id: card.id, section: 'public' });
            setSelectedCard(null);
          }}
        />
      )}
    </div>
  );
};

export default CartasStorePage;
